import React, { useRef } from "react";
import { debounce } from "throttle-debounce";
import { Button, Glyphicon, FormGroup, ControlLabel, FormControl, Row, Col, Tabs, Tab } from "react-bootstrap";
import FormControlLanguage from "../../../../UI/FormControlLanguage";
import Select from "react-select";
import InputDateTime from "../../../../InputDateTime";
import { archiveStatusTitles, streamParticipationTypeOptions } from "./lib";
import { archiveStream, checkStreamArchive, deleteStreamPoster, resetStreamArchive, updateStreamPoster } from "../../../../../../api/cp/stream";
import DeleteConfirmOverlay from "../../../../UI/DeleteConfirmOverlay";
import ConfirmOverlay from "../../../../UI/ConfirmOverlay";
import { useUserContext } from "../../../../../context";

export default ({ event, editingStream, setEditingStream, isSaving, setSaving }) => {
    const imageInputRef = useRef(null);
    const { userIsAdmin } = useUserContext();

    const handleColor = debounce(500, (value) => {
        const color = value?.replace('#', '');
        setEditingStream(stream => ({ ...stream, color }))
    });

    const handleSavePreview = (file) => {
        if (file === 'DELETE') {
            deleteStreamPoster(editingStream.id)
                .then(() => setEditingStream(stream => ({ ...stream, preview: '' })))
                .catch(e => console.error(e))
        } else {
            const formData = new FormData();
            formData.append('poster', file);
            updateStreamPoster(editingStream.id, formData)
                .then((response) => setEditingStream(stream => ({ ...stream, preview: response?.url })))
                .catch(e => console.error(e))
        }
    }

    return <div style={{ marginTop: '1rem' }}>
        <FormGroup >
            <ControlLabel>Название зала</ControlLabel>
            <FormControlLanguage
                type="text"
                values={{
                    ru: editingStream.description_ru || '',
                    en: editingStream.description_en || '',
                }}
                onChange={{
                    ru: (e) => setEditingStream(stream => ({ ...stream, description_ru: e.target.value })),
                    en: (e) => setEditingStream(stream => ({ ...stream, description_en: e.target.value })),
                }}
                disabled={isSaving}
            />
        </FormGroup>
        <FormGroup >
            <ControlLabel>Статус архивации</ControlLabel>
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControl
                    type="text"
                    value={archiveStatusTitles[editingStream.archiveStatus]}
                    readOnly
                />
                <ArchiveButton
                    id={editingStream.id}
                    archiveStatus={editingStream.archiveStatus}
                    setEditingStream={setEditingStream}
                    setSaving={setSaving}
                />
            </div>
        </FormGroup>
        <Row>
            <Col xs={4}>
                <FormGroup>
                    <ControlLabel>ID</ControlLabel>
                    <FormControl
                        type="text"
                        value={editingStream.id}
                        readOnly
                    />
                </FormGroup>
            </Col>
            <Col xs={4}>
                <FormGroup>
                    <ControlLabel>streamKey</ControlLabel>
                    <FormControl
                        type="text"
                        value={editingStream.streamKey}
                        readOnly
                    />
                </FormGroup>
            </Col>
            <Col xs={4}>
                <FormGroup>
                    <ControlLabel>streamName</ControlLabel>
                    <FormControl
                        type="text"
                        value={editingStream.name}
                        readOnly
                    />
                </FormGroup>
            </Col>
        </Row>
        <Row>
            <Col xs={6}>
                <FormGroup >
                    <ControlLabel>Время начала</ControlLabel>
                    <InputDateTime
                        value={editingStream.timeStart}
                        min={event.timeStart}
                        max={editingStream.timeStop}
                        onChange={(value) => setEditingStream(stream => ({ ...stream, timeStart: value }))}
                        disabled={!userIsAdmin || isSaving}
                        errorMessage={{ min: 'Время начала зала не может быть раньше времени начала мероприятия' }}
                    />
                </FormGroup>
            </Col>
            <Col xs={6}>
                <FormGroup >
                    <ControlLabel>Время окончания</ControlLabel>
                    <InputDateTime
                        value={editingStream.timeStop}
                        min={editingStream.timeStart}
                        max={event.timeStop}
                        onChange={(value) => setEditingStream(stream => ({ ...stream, timeStop: value }))}
                        disabled={!userIsAdmin || isSaving}
                        errorMessage={{ max: 'Время окончания зала не может быть позже времени окончания мероприятия' }}
                    />
                </FormGroup>
            </Col>
        </Row>
        <Row>
            <Col xs={6}>
                <FormGroup >
                    <ControlLabel>Формат участия</ControlLabel>
                    <Select
                        value={streamParticipationTypeOptions.find(({ value }) => editingStream.participationType === value || value === "hybrid")}
                        onChange={({ value }) => setEditingStream(stream => ({ ...stream, participationType: value }))}
                        options={streamParticipationTypeOptions}
                        isDisabled={isSaving}
                    />
                </FormGroup>
            </Col>
            <Col xs={6}>
                <FormGroup>
                    <ControlLabel>Цвет зала</ControlLabel>
                    <div>
                        <input
                            type="color"
                            value={`#${editingStream.color || 'ffffff'}`}
                            onChange={(e) => handleColor(e.target.value)}
                        />
                    </div>
                </FormGroup>
            </Col>
        </Row>
        <FormGroup>
            <ControlLabel>{editingStream.preview ? "Изменить" : "Добавить"} постер</ControlLabel>
            {editingStream.preview && <div><img src={editingStream.preview} style={{ maxWidth: '100%', maxHeight: 120 }} alt="" /></div>}
            <div style={{ marginTop: '1rem' }}>
                <Button onClick={() => imageInputRef.current?.click()}><Glyphicon glyph="folder-open" /></Button>
                {editingStream.preview && <Button onClick={() => handleSavePreview('DELETE')} bsStyle="danger"><Glyphicon glyph="trash" /></Button>}
            </div>
            <FormControl
                type="file"
                onChange={(e) => handleSavePreview(e.target.files[0] || null)}
                inputRef={ref => imageInputRef.current = ref}
                style={{ display: 'none' }}
            />
        </FormGroup>
    </div>
}

const ArchiveButton = ({ id, archiveStatus, setEditingStream, setSaving }) => {
    const handleArchive = () => {
        setSaving(true);

        archiveStream(id)
            .catch(e => console.error(e))
            .finally(() => setSaving(false))
    }

    const handleResetArchive = () => {
        setSaving(true);

        resetStreamArchive(id)
            .then(response => setEditingStream(stream => ({ ...stream, archiveStatus: response?.archiveStatus })))
            .catch(e => console.error(e))
            .finally(() => setSaving(false))
    }

    const handleCheckArchive = () => {
        checkStreamArchive(id)
            .then(response => {
                if (response?.archiveStatus && archiveStatus !== response.archiveStatus) {
                    setEditingStream(stream => ({ ...stream, archiveStatus: response.archiveStatus }))
                }
            })
            .catch(e => console.error(e))
    }

    switch (archiveStatus) {
        case 'done':
        case 'file':
            return <ConfirmOverlay title="Вы действительно хотите отменить архивацию?" buttonText="Отменить" onConfirm={handleResetArchive}>
                <Button>Отменить архивацию</Button>
            </ConfirmOverlay>
        case 'none':
        case 'fail':
            return <DeleteConfirmOverlay
                title="Вы уверены что хотите завершить трансляцию в этом зале и начать процедуру архивации?"
                buttonText="Архивировать"
                onConfirm={handleArchive}>
                <Button>Архивировать</Button>
            </DeleteConfirmOverlay>
        case 'process':
            return <Button onClick={handleCheckArchive}>Проверить архивацию</Button>
        default:
            return null
    }
}